// @ts-nocheck
import * as d3 from 'd3';
import * as d3dag from 'd3-dag';

import merge from 'lodash.merge';

import {fetchTracingGraph} from 'api';
import {colors} from 'theme';
import {OverrideTraceGraphConfig, TraceGraphConfig, TracingNode, TracingNodeStatement} from 'types';

export interface BuildGraphProps {
    data: TracingNode | null;
    overrideConfig?: OverrideTraceGraphConfig;
    period: [Date | null, Date | null];
    name?: string;
    clientId?: number;
    isAdmin?: boolean;
    statements?: TracingNodeStatement[];
}

type CustomSelection = d3.Selection<SVGGElement, unknown, SVGGElement, unknown>;

function arrowTransform({points}: {points: readonly (readonly [number, number])[]}) {
    const [[x1, y1], [x2, y2]] = points.slice(-2);
    const angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI + 90;
    return `translate(${x2}, ${y2}) rotate(${angle})`;
}

/* ===================== CONFIG ===================== */
function getDefaultConfig(): TraceGraphConfig {
    return {
        disableZoom: false,
        disableToggle: false,
        heightFixed: false,
        initialZoom: 1,
        width: 1272,
        height: 420,
        margin: {top: 40, right: 40, bottom: 40, left: 40},
        card: {
            width: 360,
            height: 180,
            padding: {x: 24, y: 16},
            icon: {
                size: 48,
                rightGap: 32,
                borderRadius: 8,
            },
            toggle: {size: 40},
            borderRadius: 24,
            verticalSpace: 54,
            horizontalSpace: 124,
        },
        color: {
            iconBg: '#F0F4F7',
            main: {
                default: '#E1E8EF',
                active: '#6466E9',
            },
        },
    };
}

export async function build({
    data,
    period,
    overrideConfig = {},
    isAdmin,
    statements,
    name,
    clientId,
}: BuildGraphProps) {
    if (!data) return;

    const config = merge(getDefaultConfig(), overrideConfig);

    const svg = d3.select<SVGSVGElement, unknown>('#trace-graph');
    svg.selectAll('*').remove();

    const nodeSize: [number, number] = [config.card.width, config.card.height];
    const shape = d3dag.tweakShape(nodeSize, d3dag.shapeRect);

    const layout = d3dag
        .sugiyama()
        .nodeSize(nodeSize)
        .gap([config.card.horizontalSpace + 30, config.card.verticalSpace + 40])
        .tweaks([shape]);

    const line = d3.line<d3dag.Point>().curve(d3.curveMonotoneY);

    const graph = d3dag.graph<TracingNode, undefined>();
    const nodeMap = new Map<string, d3dag.MutGraphNode<TracingNode, undefined>>();

    function hasLink(source: d3dag.MutGraphNode<any, any>, target: d3dag.MutGraphNode<any, any>) {
        for (const link of source.childLinks()) {
            if (link.target === target) return true;
        }
        return false;
    }

    function addNode(
        nodeData: TracingNode,
        parent?: d3dag.MutGraphNode<TracingNode, undefined>,
        isParent = false,
    ) {
        let node: d3dag.MutGraphNode<TracingNode, undefined>;

        if (nodeMap.has(nodeData.id)) {
            node = nodeMap.get(nodeData.id)!;
        } else {
            node = graph.node(nodeData);
            nodeMap.set(nodeData.id, node);

            nodeData.children?.forEach((child) => {
                if (child.id !== nodeData.id) {
                    addNode(child, node, false);
                }
            });

            nodeData.parents?.forEach((parentNode) => {
                if (parentNode.id !== nodeData.id) {
                    addNode(parentNode, node, true);
                }
            });
        }

        /* ---------- LINKING ---------- */
        if (parent && parent.data.id !== nodeData.id) {
            if (isParent) {
                if (!hasLink(node, parent)) {
                    parent.parent(node);
                }
            } else if (!hasLink(parent, node)) {
                parent.child(node);
            }
        }

        return node;
    }

    addNode(data);

    function detectCycles(graph: d3dag.MutGraph<any, any>) {
        const visited = new Set<string>();
        const stack = new Set<string>();
        const cycles: Array<[string, string]> = [];

        function dfs(node: d3dag.MutGraphNode<any, any>) {
            const {id} = node.data;
            if (stack.has(id)) return;
            if (visited.has(id)) return;

            visited.add(id);
            stack.add(id);

            for (const link of node.childLinks()) {
                const targetId = link.target.data.id;
                if (stack.has(targetId)) {
                    cycles.push([id, targetId]);
                } else dfs(link.target);
            }

            stack.delete(id);
        }

        graph.nodes().forEach(dfs);

        cycles.forEach(([fromId, toId], idx) => {
            const from = nodeMap.get(fromId)!;
            const to = nodeMap.get(toId)!;

            from?.removeChild?.(to);

            const virtualNode = graph.node({
                id: `__cycle_${fromId}_${toId}_${idx}`,
                name: `__name_${fromId}_${toId}`,
                isVirtual: true,
            });

            from?.child(virtualNode);
            virtualNode.child(to);
        });
    }

    async function onClick(node: d3dag.MutGraphNode<TracingNode, undefined>) {
        const nodeData = node.data;

        try {
            const subgraph = await fetchTracingGraph({
                id: nodeData.id,
                gtin: nodeData.gtin,
                period,
            });

            let changed = false;

            if (subgraph?.children?.length) {
                nodeData.children = nodeData.children ?? [];
                subgraph.children.forEach((child: TracingNode) => {
                    if (!nodeData.children?.some((c) => c.id === child.id)) {
                        nodeData.children.push(child);
                        addNode(child, node, false);
                        changed = true;
                    }
                });
            }

            if (subgraph?.parents?.length) {
                nodeData.parents = nodeData.parents ?? [];
                subgraph.parents.forEach((parentNode: TracingNode) => {
                    if (!nodeData.parents?.some((p) => p.id === parentNode.id)) {
                        nodeData.parents?.push(parentNode);
                        addNode(parentNode, node, true);
                        changed = true;
                    }
                });
            }

            if (!changed) return;

            drawGraph({animate: true});
            centerOnNode(node, 0.5);
        } catch (e) {
            console.error(e);
        }
    }

    /* ------------------- ZOOM ------------------- */
    const zoom = d3
        .zoom<SVGSVGElement, unknown>()
        .scaleExtent([0.3, 3])
        .on('zoom', (event) => {
            g.attr('transform', event.transform);
        });

    svg.call(zoom);

    function centerGraph(width: number, height: number) {
        const svgNode = svg.node();
        if (!svgNode) return;

        const svgWidth = svgNode.clientWidth;
        const svgHeight = svgNode.clientHeight;

        const scale = Math.min(
            svgWidth / (width + config.margin.left + config.margin.right),
            svgHeight / (height + config.margin.top + config.margin.bottom),
            1,
        );

        const translateX = (svgWidth - width * scale) / 2;
        const translateY = (svgHeight - height * scale) / 2;

        svg.call(zoom.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale));
    }

    function centerOnNode(node: d3dag.MutGraphNode<TracingNode, undefined>, scale = 1) {
        const svgNode = svg.node();
        if (!svgNode) return;

        const svgWidth = svgNode.clientWidth;
        const svgHeight = svgNode.clientHeight;

        const {x, y} = node;

        const transform = d3.zoomIdentity
            .translate(
                svgWidth / 2 - x * scale - 3 * config.margin.left,
                svgHeight / 2 - y * scale - 3 * config.margin.top,
            )
            .scale(scale);

        svg.transition()
            .duration(400)
            .call(zoom.transform, transform);
    }

    const g = svg.append('g');

    /* ======================= RENDER ======================= */
    function focusChain(node: d3dag.MutGraphNode<TracingNode, undefined> | null) {
        const nodesSelect = g.selectAll<SVGGElement, d3dag.MutGraphNode<TracingNode, undefined>>(
            'g.tg-card',
        );

        const linksSelect = g.selectAll<SVGPathElement, d3dag.MutGraphLink<TracingNode, undefined>>(
            'path.tg-link',
        );

        const arrowsSelect = g.selectAll<
            SVGPathElement,
            d3dag.MutGraphLink<TracingNode, undefined>
        >('path.tg-arrow');

        if (!node) {
            nodesSelect.classed('tg-active tg-dimmed', false);
            linksSelect.classed('tg-active tg-dimmed', false);
            arrowsSelect.classed('tg-active tg-dimmed', false);
            return;
        }

        const activeNodeIds = new Set<string>();
        const activeLinks = new Set<string>();

        const stack = [node];

        while (stack.length) {
            const n = stack.pop()!;
            const {id} = n.data;

            if (activeNodeIds.has(id)) continue;
            activeNodeIds.add(id);

            for (const link of n.parentLinks()) {
                activeLinks.add(`${link.source.data.id}-${link.target.data.id}`);
                stack.push(link.source);
            }
        }

        /* ---------- NODES ---------- */
        nodesSelect.classed('tg-active', (d) => activeNodeIds.has(d.data.id));
        nodesSelect.classed('tg-dimmed', (d) => !activeNodeIds.has(d.data.id));

        /* ---------- LINKS ---------- */
        linksSelect
            .classed('tg-active', (l) => activeLinks.has(`${l.source.data.id}-${l.target.data.id}`))
            .classed(
                'tg-dimmed',
                (l) => !activeLinks.has(`${l.source.data.id}-${l.target.data.id}`),
            )
            .filter((l) => activeLinks.has(`${l.source.data.id}-${l.target.data.id}`))
            .raise();

        /* ---------- ARROWS ---------- */
        arrowsSelect
            .classed('tg-active', (l) => activeLinks.has(`${l.source.data.id}-${l.target.data.id}`))
            .classed(
                'tg-dimmed',
                (l) => !activeLinks.has(`${l.source.data.id}-${l.target.data.id}`),
            )
            .filter((l) => activeLinks.has(`${l.source.data.id}-${l.target.data.id}`))
            .raise();
    }

    function addBackground(selection: CustomSelection) {
        selection
            .append('rect')
            .classed('tg-background', true)
            .classed('owner', (d) => d.data.name.toLowerCase() === name?.toLowerCase())
            .classed('dashed', (d) => d.data.isDropout || d.data.isSold)
            .style('--highlight', (d) => {
                if (d.data.isDropout) return colors.danger;
                if (d.data.isSold) return colors.success;
                return '#e1e8ef';
            })
            .attr('width', config.card.width)
            .attr('height', config.card.height)
            .attr('rx', config.card.borderRadius)
            .attr('ry', config.card.borderRadius)
            .attr('fill', '#fff')
            .attr('stroke', config.color.main.default)
            .attr('stroke-width', 1);
    }

    function addIcon(selection: CustomSelection) {
        const g = selection
            .append('g')
            .attr(
                'transform',
                `translate(
                ${config.card.padding.x},
                ${config.card.height / 2 - config.card.icon.size / 2}
            )`,
            )
            .attr('pointer-events', 'none');

        g.append('rect')
            .attr('width', config.card.icon.size)
            .attr('height', config.card.icon.size)
            .attr('rx', config.card.icon.borderRadius)
            .attr('ry', config.card.icon.borderRadius)
            .attr('fill', config.color.iconBg);

        g.append('image')
            .attr(
                'transform',
                `translate(
                ${config.card.icon.size / 4},
                ${config.card.icon.size / 4}
            )`,
            )
            .attr('width', config.card.icon.size / 2)
            .attr('height', config.card.icon.size / 2)
            .attr(
                'href',
                (d) =>
                    `/icons/tracing/${
                        d.data.isSold ? 'SOLD' : d.data.isDropout ? 'DROPOUT' : 'transport'
                    }.svg`,
            );
    }

    function addContent(selection: CustomSelection) {
        const wrapper = selection
            .append('foreignObject')
            .classed('tg-content', true)
            // .attr('x', config.card.padding.x + config.card.icon.size + config.card.icon.rightGap)
            // .attr('y', config.card.padding.y)
            .attr('width', config.card.width)
            .attr('height', config.card.height);

        const body = wrapper
            .append('xhtml:div')
            .style('display', 'flex')
            .style('flex-direction', 'column');

        body.append('span')
            .classed('tg-name', true)
            .style('--highlight', (d) => {
                if (d.data.isDropout) return colors.danger;
                if (d.data.isSold) return colors.success;
                return colors.brandDark;
            })
            .html((d) =>
                d.data.isSold
                    ? 'Consumers'
                    : d.data.isDropout
                    ? 'Rejection'
                    : `<div>${d.data.name}</div>${
                          name?.toLowerCase() === d.data.name.toLowerCase()
                              ? '<span>Your company</span>'
                              : ''
                      }`,
            );

        statements?.forEach((statement) => {
            body.append('div')
                .classed('tg-statement', true)
                .style('color', colors.textSecondary)
                .style('padding-left', `${config.card.icon.size + config.card.icon.rightGap}px`)
                .text((d) => `${statement.label} ${statement.transform(d.data)}`);
        });
    }

    function addToggle(selection: CustomSelection, onClick: (d: unknown) => void) {
        const g = selection
            .filter((d) => !d.data.isDropout && !d.data.isSold && d.data.childrenCount)
            .append('g')
            .classed('tg-toggle', true)
            .attr(
                'transform',
                `translate(
                ${config.card.width - config.card.toggle.size * 1.25},
                ${(config.card.height - config.card.toggle.size) / 2}
            )`,
            );

        const rect = g
            .append('rect')
            .attr('width', config.card.toggle.size)
            .attr('height', config.card.toggle.size)
            .attr('fill', 'transparent');

        if (!config.disableToggle) {
            // eslint-disable-next-line func-names
            rect.on('click', function(_, d) {
                d3.select(this.parentNode as SVGGElement).remove();
                onClick(d);
            });
        }

        g.append('path')
            .attr(
                'd',
                'M25.833 21.178L17.012 30C16.361 30.651 15.305 30.651 14.653 30C14.002 29.349 14.002 28.293 14.653 27.642L22.298 20L14.653 12.357C14.002 11.706 14.002 10.65 14.653 9.999C15.305 9.348 16.361 9.348 17.012 9.999L25.833 18.822C26.484 19.473 26.484 20.529 25.833 21.178Z',
            )
            .attr('fill-rule', 'evenodd')
            .attr('clip-rule', 'evenodd')
            .attr('fill', config.color.main.default)
            .style('pointer-events', 'none');
    }

    /* ------------------- DRAW ------------------- */
    function drawGraph({animate = false, init = false}) {
        detectCycles(graph);
        const {width, height} = layout(graph);
        svg.attr('height', height + config.margin.top + config.margin.bottom);

        if (init) centerGraph(width, height);

        const visibleLinks = (list: d3dag.MutGraphLink<TracingNode, undefined>[]) =>
            list.filter((item) => !item.source.data.isVirtual && !item.target.data.isVirtual);

        /* ---------- LINKS ---------- */
        const links = g
            .selectAll<SVGPathElement, d3dag.MutGraphLink<TracingNode, undefined>>('path.tg-link')
            .data(visibleLinks(graph.links()), (d) => `${d.source.data.id}-${d.target.data.id}`);

        links.join(
            (enter) =>
                enter
                    .append('path')
                    .attr('class', 'tg-link')
                    .attr('fill', 'none')
                    .style('--highlight', (d) => {
                        if (d.target.data.isDropout) return colors.danger;
                        if (d.target.data.isSold) return colors.success;
                        return '#CBD5E1';
                    })
                    .attr('stroke-width', 2)
                    .attr('d', (d) => line(d.points)),
            (update) =>
                update.call((u) =>
                    animate
                        ? u
                              .transition()
                              .duration(400)
                              .attr('d', (d) => line(d.points))
                        : u.attr('d', (d) => line(d.points)),
                ),
            (exit) => exit.remove(),
        );

        /* ---------- NODES ---------- */
        const nodes = g
            .selectAll<SVGGElement, d3dag.MutGraphNode<TracingNode, undefined>>('g.tg-card')
            .data(
                graph.nodes().filter((n) => !n.data.isVirtual),
                (d) => d.data.id,
            );

        const nodesEnter = nodes
            .enter()
            .append('g')
            .attr('class', 'tg-card')
            .attr(
                'transform',
                (d) => `translate(${d.x - config.card.width / 2},${d.y - config.card.height / 2})`,
            )
            .style('cursor', 'pointer')
            .on('mouseenter', (_, d) => focusChain(d))
            .on('mouseleave', () => focusChain(null));

        addBackground(nodesEnter);
        addIcon(nodesEnter);
        addContent(nodesEnter);
        addToggle(nodesEnter, onClick);

        nodes.merge(nodesEnter).call((sel) =>
            animate
                ? sel
                      .transition()
                      .duration(400)
                      .attr(
                          'transform',
                          (d) =>
                              `translate(${d.x - config.card.width / 2},${d.y -
                                  config.card.height / 2})`,
                      )
                : sel.attr(
                      'transform',
                      (d) =>
                          `translate(${d.x - config.card.width / 2},${d.y -
                              config.card.height / 2})`,
                  ),
        );

        nodes.exit().remove();

        /* ---------- ARROWS ---------- */
        const arrowSize = 120;
        const arrowLen = Math.sqrt((4 * arrowSize) / Math.sqrt(3));
        const arrow = d3
            .symbol()
            .type(d3.symbolTriangle)
            .size(arrowSize);

        const arrows = g
            .selectAll<SVGPathElement, d3dag.MutGraphLink<TracingNode, undefined>>('path.tg-arrow')
            .data(visibleLinks(graph.links(), (d) => `${d.source.data.id}-${d.target.data.id}`));

        arrows.join(
            (enter) =>
                enter
                    .append('path')
                    .attr('class', 'tg-arrow')
                    .attr('d', arrow)
                    .style('--highlight', (d) => {
                        if (d.target.data.isDropout) return colors.danger;
                        if (d.target.data.isSold) return colors.success;
                        return '#CBD5E1';
                    })
                    .attr('stroke', 'white')
                    .attr('stroke-width', 2)
                    .attr('stroke-dasharray', `${arrowLen},${arrowLen}`)
                    .attr('transform', arrowTransform)
                    .attr('opacity', 0)
                    .call((enter) =>
                        enter
                            .transition()
                            .duration(400)
                            .attr('opacity', 1),
                    ),
            (update) =>
                update.call((u) =>
                    u
                        .transition()
                        .duration(400)
                        .attr('transform', arrowTransform)
                        .style('--highlight', (d) => {
                            if (d.target.data.isDropout) return colors.danger;
                            if (d.target.data.isSold) return colors.success;
                            return '#CBD5E1';
                        }),
                ),
            (exit) =>
                exit.call((e) =>
                    e
                        .transition()
                        .duration(400)
                        .attr('opacity', 0)
                        .remove(),
                ),
        );
    }

    drawGraph({init: true});
}
